import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import colors from 'src/style/colors';
import { mediaQueries } from 'src/style/breakpoints';
import Star from 'src/components/Icons/Star';
import Check from 'src/components/Icons/tick.svg';

const HeroContentLeft = ({
  title,
  tags,
  tagVariant,
  tagsBeforeTitle,
  metaLine,
  sessionBadge,
  subtitle,
  infoBox,
  checklistItems,
  statsLine1,
  statsRating,
  statsRatingLabel,
  showLogos = true,
}) => {
  const tagsBlock = tags && tags.length > 0 && (
        <div
          css={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            '@media only screen and (max-width: 1199px)': {
              justifyContent: 'center',
            },
          }}
        >
          {tags.map((tag, index) => (
            <span
              key={index}
              css={
                tagVariant === 'outlined' && !tag.color
                  ? {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.4)',
                      color: colors.white,
                      fontSize: 14,
                      fontWeight: 600,
                      padding: '7px 16px',
                      borderRadius: 8,
                      lineHeight: '20px',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }
                  : tagVariant === 'outlined' && tag.color
                    ? {
                        backgroundColor: tag.color,
                        border: `1px solid ${tag.color}`,
                        color: colors.white,
                        fontSize: 14,
                        fontWeight: 600,
                        fontStyle: 'italic',
                        padding: '7px 16px',
                        borderRadius: 8,
                        lineHeight: '20px',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }
                    : {
                        backgroundColor: tag.color || 'rgba(255,255,255,0.2)',
                        color:
                          tag.color === colors.yellow || tag.color === colors.lightGrey
                            ? colors.purpleDark
                            : colors.white,
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        padding: '5px 10px',
                        borderRadius: 3,
                      }
              }
            >
              {tag.text}
            </span>
          ))}
        </div>
      );

  return (
    <div
      css={{
        paddingRight: 35,
        flex: '0 0 60%',
        maxWidth: '60%',
        color: colors.white,
        '@media only screen and (max-width: 1199px)': {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          width: '100%',
          flex: '1',
          maxWidth: '100%',
        },
      }}
    >
      {/* Stats Badge */}
      {statsLine1 && (
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginBottom: '1.5rem',
            '@media only screen and (max-width: 1199px)': {
              alignItems: 'center',
            },
          }}
        >
          <div
            css={{
              fontSize: '1rem',
              marginBottom: '0.5rem',
            }}
          >
            {statsLine1}
          </div>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
            }}
          >
            <span css={{ color: colors.yellow, fontSize: '1rem' }}>
              <Star width="16" height="16" />
              <Star width="16" height="16" />
              <Star width="16" height="16" />
              <Star width="16" height="16" />
              <Star width="16" height="16" />
            </span>
            <span>
              <strong>{statsRating}</strong> · {statsRatingLabel}
            </span>
          </div>
        </div>
      )}

      {/* Tags before title */}
      {tagsBeforeTitle && tagsBlock}

      {/* Title */}
      <h1
        css={{
          fontSize: '3rem',
          lineHeight: 1.1,
          marginBottom: '1.5rem',
          fontWeight: 800,
          '@media only screen and (max-width: 1199px)': {
            textAlign: 'center',
          },
          [mediaQueries.phoneOnly]: {
            fontSize: '2.25rem',
          },
        }}
      >
        {title}
      </h1>

      {/* Tags after title */}
      {!tagsBeforeTitle && tagsBlock}

      {/* Meta line */}
      {metaLine && (
        <p
          css={{
            fontSize: '1.1rem',
            marginBottom: '1rem',
            color: 'rgba(255, 255, 255, 0.6)',
            fontWeight: 500,
            '@media only screen and (max-width: 1199px)': {
              textAlign: 'center',
            },
          }}
        >
          {metaLine}
        </p>
      )}

      {/* Subtitle with yellow highlights */}
      <p
        css={{
          fontSize: '1.25rem',
          marginBottom: '2rem',
          lineHeight: 1.6,
          '@media only screen and (max-width: 1199px)': {
            textAlign: 'center',
          },
          [mediaQueries.phoneOnly]: {
            fontSize: '1.125rem',
          },
        }}
      >
        {subtitle}
      </p>

      {/* Purple box */}
      {infoBox && (
        <div
          css={{
            backgroundColor: 'rgba(138, 125, 222, 0.3)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 12,
            padding: '1.5rem',
            marginBottom: '2rem',
            '@media only screen and (max-width: 1199px)': {
              textAlign: 'center',
            },
          }}
        >
          <div
            css={{
              fontSize: 17,
              lineHeight: '24px',
              color: colors.white,
              margin: 0,
              [mediaQueries.phoneOnly]: {
                fontSize: 16,
                lineHeight: '22px',
              },
            }}
          >
            {infoBox}
          </div>
        </div>
      )}

      {/* Session badge */}
      {sessionBadge && (
        <div
          css={{
            backgroundColor: 'rgba(252, 192, 26, 0.15)',
            backdropFilter: 'blur(10px)',
            border: `1px solid rgba(252, 192, 26, 0.5)`,
            borderRadius: 12,
            padding: '1rem 1.25rem',
            marginBottom: '2rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: 15,
            fontWeight: 600,
            color: colors.white,
            '@media only screen and (max-width: 1199px)': {
              textAlign: 'left',
            },
            [mediaQueries.phoneOnly]: {
              fontSize: 14,
              padding: '0.75rem 1rem',
            },
          }}
        >
          <span>📅</span>
          {sessionBadge}
        </div>
      )}

      {/* Checklist */}
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: checklistItems.some((item) => item.middle)
            ? '1fr 1fr 1fr'
            : '1fr 1fr',
          gap: '1rem',
          marginBottom: '2rem',
          [mediaQueries.phoneOnly]: {
            gap: '0.75rem',
            gridTemplateColumns: checklistItems.some((item) => item.middle)
              ? '1fr 1fr 1fr'
              : '1fr 1fr',
            textAlign: 'left',
          },
        }}
      >
        {checklistItems.map((item, index) => (
          <React.Fragment key={index}>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              <div
                css={{
                  width: 24,
                  height: 24,
                  backgroundColor: colors.yellow,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '0.875rem',
                }}
              >
                ✓
              </div>
              <span
                css={{
                  fontSize: 17,
                  color: colors.white,
                  [mediaQueries.phoneOnly]: {
                    fontSize: 16,
                  },
                }}
              >
                <strong>{item.left.split(' ')[0]}</strong>{' '}
                {item.left.split(' ').slice(1).join(' ')}
              </span>
            </div>
            {item.middle && (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <div
                  css={{
                    width: 24,
                    height: 24,
                    backgroundColor: colors.yellow,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: '0.875rem',
                  }}
                >
                  ✓
                </div>
                <span
                  css={{
                    fontSize: 17,
                    color: colors.white,
                    [mediaQueries.phoneOnly]: {
                      fontSize: 16,
                    },
                  }}
                >
                  <strong>{item.middle.split(' ')[0]}</strong>{' '}
                  {item.middle.split(' ').slice(1).join(' ')}
                </span>
              </div>
            )}
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              <div
                css={{
                  width: 24,
                  height: 24,
                  backgroundColor: colors.yellow,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '0.875rem',
                }}
              >
                ✓
              </div>
              <span
                css={{
                  fontSize: 17,
                  color: colors.white,
                  [mediaQueries.phoneOnly]: {
                    fontSize: 16,
                  },
                }}
              >
                <strong>{item.right.split(' ')[0]}</strong>{' '}
                {item.right.split(' ').slice(1).join(' ')}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Logos CPF, France Travail, Transitions Pro */}
      {showLogos && <div
        css={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
          flexWrap: 'wrap',
          '@media only screen and (max-width: 1199px)': {
            justifyContent: 'center',
          },
        }}
      >
        <div
          css={{
            width: 100,
            height: 100,
            backgroundColor: colors.white,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.75rem',
          }}
        >
          <StaticImage
            src="../../../data/images/institutionals/moncompteformation_rvb.png"
            alt="Mon Compte Formation"
            css={{ width: '100%', height: '100%' }}
            imgStyle={{ objectFit: 'contain' }}
          />
        </div>
        <div
          css={{
            width: 100,
            height: 100,
            backgroundColor: colors.white,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.75rem',
          }}
        >
          <StaticImage
            src="../../../data/images/institutionals/France-travail.png"
            alt="France Travail"
            css={{ width: '100%', height: '100%' }}
            imgStyle={{ objectFit: 'contain' }}
          />
        </div>
        <div
          css={{
            width: 100,
            height: 100,
            backgroundColor: colors.white,
            borderRadius: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.75rem',
          }}
        >
          <StaticImage
            src="../../../data/images/institutionals/logo-transitions-pro.png"
            alt="Transitions Pro"
            css={{ width: '100%', height: '100%' }}
            imgStyle={{ objectFit: 'contain' }}
          />
        </div>
      </div>}
    </div>
  );
};

export default HeroContentLeft;
